import { useState, useEffect } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import axios from "axios";
import Navbar from "@/components/Navbar";
import { Button } from "@/components/ui/button";
import { useTranslation } from "react-i18next";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function TeacherReportsPage({ user, onLogout }) {
  const { t } = useTranslation();
  const { classId } = useParams();
  const navigate = useNavigate();
  
  const [loading, setLoading] = useState(true);
  const [report, setReport] = useState(null);
  const [sessions, setSessions] = useState([]);
  const [classes, setClasses] = useState([]);
  const [selectedClass, setSelectedClass] = useState(classId || "");
  const [activeTab, setActiveTab] = useState("students"); // students, sessions
  const [exportingCSV, setExportingCSV] = useState(false);
  const [exportingPDF, setExportingPDF] = useState(false);
  const [error, setError] = useState("");

  // Fetch classes list for dropdown
  useEffect(() => {
    const fetchClasses = async () => {
      try {
        const response = await axios.get(`${API}/classes`);
        setClasses(response.data);
        
        // Auto-select first class if none selected
        if (!selectedClass && response.data.length > 0) {
          setSelectedClass(response.data[0].id);
        }
      } catch (err) {
        console.error("Failed to fetch classes:", err);
        setError("Failed to load classes");
      }
    };
    fetchClasses();
  }, [selectedClass]);

  // Fetch report when class changes
  useEffect(() => {
    if (!selectedClass) {
      setLoading(false);
      return;
    }
    
    const fetchReport = async () => {
      setLoading(true);
      setError("");
      try {
        const [reportRes, sessionsRes] = await Promise.all([
          axios.get(`${API}/game/teacher/reports/${selectedClass}?educator_id=${user.id}`),
          axios.get(`${API}/game/teacher/reports/${selectedClass}/sessions?educator_id=${user.id}`)
        ]);
        setReport(reportRes.data);
        setSessions(sessionsRes.data.sessions || []);
      } catch (err) {
        console.error("Failed to fetch report:", err);
        setError("Failed to load report data");
      } finally {
        setLoading(false);
      }
    };
    
    fetchReport();
  }, [selectedClass, user.id]);

  // Handle CSV export
  const handleExportCSV = async () => {
    if (!selectedClass) return;
    
    setExportingCSV(true);
    try {
      const response = await axios.get(
        `${API}/game/teacher/reports/${selectedClass}/export/csv?educator_id=${user.id}`
      );
      
      // Create and download the CSV file
      const blob = new Blob([response.data.content], { type: "text/csv" });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = response.data.filename;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (err) {
      console.error("Failed to export CSV:", err);
      setError("Failed to export CSV");
    } finally {
      setExportingCSV(false);
    }
  };

  // Handle PDF export
  const handleExportPDF = async () => {
    if (!selectedClass) return;
    
    setExportingPDF(true);
    setError("");
    try {
      const response = await axios.get(
        `${API}/game/teacher/reports/${selectedClass}/export/pdf?educator_id=${user.id}`
      );
      
      // Decode base64 and create blob
      const byteCharacters = atob(response.data.content);
      const byteNumbers = new Array(byteCharacters.length);
      for (let i = 0; i < byteCharacters.length; i++) {
        byteNumbers[i] = byteCharacters.charCodeAt(i);
      }
      const byteArray = new Uint8Array(byteNumbers);
      const blob = new Blob([byteArray], { type: "application/pdf" });
      
      // Download the PDF file
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = response.data.filename;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (err) {
      console.error("Failed to export PDF:", err);
      setError("Failed to export PDF");
    } finally {
      setExportingPDF(false);
    }
  };

  // Format date for display
  const formatDate = (dateStr) => {
    if (!dateStr) return "Never";
    const date = new Date(dateStr);
    return date.toLocaleDateString() + " " + date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  // Get module badge color
  const getModuleBadgeColor = (moduleId) => {
    const colors = {
      module_1: "bg-yellow-100 text-yellow-800",
      module_2: "bg-blue-100 text-blue-800",
      module_3: "bg-green-100 text-green-800",
      module_4: "bg-pink-100 text-pink-800",
      module_5: "bg-emerald-100 text-emerald-800",
      module_6: "bg-orange-100 text-orange-800",
      module_7: "bg-purple-100 text-purple-800"
    };
    return colors[moduleId] || "bg-gray-100 text-gray-800";
  };

  // Get module display name
  const getModuleName = (moduleId) => {
    const names = {
      module_1: "Idea Lab",
      module_2: "Bird Squad",
      module_3: "Blueprint",
      module_4: "Brand Builder",
      module_5: "Money Matters",
      module_6: "Launch Day",
      module_7: "Dream Big"
    };
    return names[moduleId] || moduleId;
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-yellow-50 to-orange-50">
      <Navbar user={user} onLogout={onLogout} />
      
      <main className="container mx-auto px-4 py-8" data-testid="teacher-reports-page">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-8">
          <div>
            <h1 className="text-3xl font-bold text-gray-800">
              🎮 Game Reports
            </h1>
            <p className="text-gray-600 mt-1">
              Track student participation, scores, and game performance
            </p>
          </div>
          
          <div className="mt-4 md:mt-0 flex items-center gap-4">
            {/* Class Selector */}
            <select
              value={selectedClass}
              onChange={(e) => setSelectedClass(e.target.value)}
              className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-yellow-500 focus:border-transparent"
              data-testid="class-selector"
            >
              <option value="">Select a class</option>
              {classes.map((cls) => (
                <option key={cls.id} value={cls.id}>
                  {cls.name}
                </option>
              ))}
            </select>
            
            {/* Export Buttons */}
            <div className="flex gap-2">
              <Button
                onClick={handleExportCSV}
                disabled={!selectedClass || exportingCSV || loading}
                className="bg-green-600 hover:bg-green-700 text-white"
                data-testid="export-csv-btn"
              >
                {exportingCSV ? (
                  <>
                    <span className="animate-spin mr-2">⏳</span>
                    CSV...
                  </>
                ) : (
                  <>📥 CSV</>
                )}
              </Button>
              
              <Button
                onClick={handleExportPDF}
                disabled={!selectedClass || exportingPDF || loading}
                className="bg-red-600 hover:bg-red-700 text-white"
                data-testid="export-pdf-btn"
              >
                {exportingPDF ? (
                  <>
                    <span className="animate-spin mr-2">⏳</span>
                    PDF...
                  </>
                ) : (
                  <>📄 PDF</>
                )}
              </Button>
              
              <Link to="/game/compare-classes">
                <Button
                  className="bg-indigo-600 hover:bg-indigo-700 text-white"
                  data-testid="compare-classes-btn"
                >
                  📊 Compare Classes
                </Button>
              </Link>
            </div>
          </div>
        </div>

        {/* Error State */}
        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg mb-6">
            {error}
          </div>
        )}

        {/* Loading State */}
        {loading && (
          <div className="flex items-center justify-center py-20">
            <div className="text-center">
              <div className="text-4xl animate-bounce mb-4">📊</div>
              <p className="text-gray-600">Loading report data...</p>
            </div>
          </div>
        )}

        {/* No Class Selected */}
        {!loading && !selectedClass && (
          <div className="bg-white rounded-xl shadow-lg p-8 text-center">
            <div className="text-6xl mb-4">📚</div>
            <h2 className="text-xl font-semibold text-gray-800 mb-2">Select a Class</h2>
            <p className="text-gray-600">Choose a class from the dropdown to view game reports</p>
          </div>
        )}

        {/* Report Content */}
        {!loading && report && (
          <>
            {/* Summary Cards */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
              <div className="bg-white rounded-xl shadow p-6 text-center">
                <div className="text-3xl font-bold text-yellow-600" data-testid="total-students">
                  {report.total_students}
                </div>
                <div className="text-sm text-gray-600">Total Students</div>
              </div>
              
              <div className="bg-white rounded-xl shadow p-6 text-center">
                <div className="text-3xl font-bold text-green-600" data-testid="students-played">
                  {report.summary.students_who_played}
                </div>
                <div className="text-sm text-gray-600">Students Who Played</div>
              </div>
              
              <div className="bg-white rounded-xl shadow p-6 text-center">
                <div className="text-3xl font-bold text-blue-600" data-testid="total-games">
                  {report.summary.total_games_played}
                </div>
                <div className="text-sm text-gray-600">Total Games</div>
              </div>
              
              <div className="bg-white rounded-xl shadow p-6 text-center">
                <div className="text-3xl font-bold text-purple-600" data-testid="avg-score">
                  {report.summary.avg_score_overall}
                </div>
                <div className="text-sm text-gray-600">Avg Score</div>
              </div>
            </div>

            {/* Module Coverage */}
            <div className="bg-white rounded-xl shadow p-6 mb-8">
              <h3 className="text-lg font-semibold text-gray-800 mb-4">Module Coverage</h3>
              <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-7 gap-3">
                <div className="text-center p-3 bg-yellow-50 rounded-lg">
                  <div className="text-xl font-bold text-yellow-700">
                    {report.summary.modules_coverage.module_1 || 0}
                  </div>
                  <div className="text-xs text-yellow-600">💡 Idea Lab</div>
                </div>
                <div className="text-center p-3 bg-blue-50 rounded-lg">
                  <div className="text-xl font-bold text-blue-700">
                    {report.summary.modules_coverage.module_2 || 0}
                  </div>
                  <div className="text-xs text-blue-600">🦅 Bird Squad</div>
                </div>
                <div className="text-center p-3 bg-green-50 rounded-lg">
                  <div className="text-xl font-bold text-green-700">
                    {report.summary.modules_coverage.module_3 || 0}
                  </div>
                  <div className="text-xs text-green-600">📋 Blueprint</div>
                </div>
                <div className="text-center p-3 bg-pink-50 rounded-lg">
                  <div className="text-xl font-bold text-pink-700">
                    {report.summary.modules_coverage.module_4 || 0}
                  </div>
                  <div className="text-xs text-pink-600">🎨 Brand</div>
                </div>
                <div className="text-center p-3 bg-emerald-50 rounded-lg">
                  <div className="text-xl font-bold text-emerald-700">
                    {report.summary.modules_coverage.module_5 || 0}
                  </div>
                  <div className="text-xs text-emerald-600">💰 Money</div>
                </div>
                <div className="text-center p-3 bg-orange-50 rounded-lg">
                  <div className="text-xl font-bold text-orange-700">
                    {report.summary.modules_coverage.module_6 || 0}
                  </div>
                  <div className="text-xs text-orange-600">🚀 Launch</div>
                </div>
                <div className="text-center p-3 bg-purple-50 rounded-lg">
                  <div className="text-xl font-bold text-purple-700">
                    {report.summary.modules_coverage.module_7 || 0}
                  </div>
                  <div className="text-xs text-purple-600">⭐ Dream</div>
                </div>
              </div>
            </div>

            {/* Tab Navigation */}
            <div className="flex border-b border-gray-200 mb-6">
              <button
                onClick={() => setActiveTab("students")}
                className={`px-6 py-3 font-medium transition-colors ${
                  activeTab === "students"
                    ? "text-yellow-600 border-b-2 border-yellow-600"
                    : "text-gray-500 hover:text-gray-700"
                }`}
                data-testid="tab-students"
              >
                👩‍🎓 Students ({report.students.length})
              </button>
              <button
                onClick={() => setActiveTab("sessions")}
                className={`px-6 py-3 font-medium transition-colors ${
                  activeTab === "sessions"
                    ? "text-yellow-600 border-b-2 border-yellow-600"
                    : "text-gray-500 hover:text-gray-700"
                }`}
                data-testid="tab-sessions"
              >
                🎮 Game Sessions ({sessions.length})
              </button>
            </div>

            {/* Students Table */}
            {activeTab === "students" && (
              <div className="bg-white rounded-xl shadow overflow-hidden">
                {report.students.length === 0 ? (
                  <div className="p-8 text-center text-gray-500">
                    <div className="text-4xl mb-2">📭</div>
                    <p>No students in this class yet</p>
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full" data-testid="students-table">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Student
                          </th>
                          <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Games
                          </th>
                          <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Points
                          </th>
                          <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Avg Score
                          </th>
                          <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Time Spent
                          </th>
                          <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Modules
                          </th>
                          <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Badges
                          </th>
                          <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Last Active
                          </th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {report.students.map((student, index) => (
                          <tr 
                            key={student.student_id}
                            className={index % 2 === 0 ? "bg-white" : "bg-gray-50"}
                            data-testid={`student-row-${student.student_id}`}
                          >
                            <td className="px-6 py-4 whitespace-nowrap">
                              <div className="flex items-center">
                                <div className="flex-shrink-0 h-10 w-10 bg-yellow-100 rounded-full flex items-center justify-center">
                                  {index < 3 && student.total_points > 0 ? (
                                    <span className="text-lg">
                                      {index === 0 ? "🥇" : index === 1 ? "🥈" : "🥉"}
                                    </span>
                                  ) : (
                                    <span className="text-yellow-600 font-bold">
                                      {student.student_name.charAt(0).toUpperCase()}
                                    </span>
                                  )}
                                </div>
                                <div className="ml-4">
                                  <div className="text-sm font-medium text-gray-900">
                                    {student.student_name}
                                  </div>
                                  <div className="text-sm text-gray-500">
                                    {student.email}
                                  </div>
                                </div>
                              </div>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-center">
                              <span className="text-sm font-medium text-gray-900">
                                {student.games_played}
                              </span>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-center">
                              <span className="text-sm font-bold text-yellow-600">
                                {student.total_points}
                              </span>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-center">
                              <span className="text-sm text-gray-900">
                                {student.avg_score}
                              </span>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-center">
                              <span className="text-sm text-gray-600">
                                {student.time_spent_minutes} min
                              </span>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-center">
                              <div className="flex justify-center gap-1 flex-wrap">
                                {student.completed_modules.length > 0 ? (
                                  student.completed_modules.map((mod) => (
                                    <span
                                      key={mod}
                                      className={`px-2 py-1 text-xs rounded-full ${getModuleBadgeColor(mod)}`}
                                    >
                                      {getModuleName(mod).split(" ")[0]}
                                    </span>
                                  ))
                                ) : (
                                  <span className="text-gray-400 text-sm">—</span>
                                )}
                              </div>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-center">
                              <span className="text-sm text-gray-900">
                                {student.badges_earned > 0 ? (
                                  <span className="bg-purple-100 text-purple-800 px-2 py-1 rounded-full">
                                    🏆 {student.badges_earned}
                                  </span>
                                ) : (
                                  <span className="text-gray-400">—</span>
                                )}
                              </span>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-center">
                              <span className="text-sm text-gray-500">
                                {student.last_played ? formatDate(student.last_played) : "Never"}
                              </span>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            )}

            {/* Sessions Table */}
            {activeTab === "sessions" && (
              <div className="bg-white rounded-xl shadow overflow-hidden">
                {sessions.length === 0 ? (
                  <div className="p-8 text-center text-gray-500">
                    <div className="text-4xl mb-2">🎮</div>
                    <p>No game sessions recorded yet</p>
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full" data-testid="sessions-table">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Game Code
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Module
                          </th>
                          <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Mode
                          </th>
                          <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Participants
                          </th>
                          <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Total Points
                          </th>
                          <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Avg Score
                          </th>
                          <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Date
                          </th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {sessions.map((session, index) => (
                          <tr 
                            key={session.game_code}
                            className={index % 2 === 0 ? "bg-white" : "bg-gray-50"}
                            data-testid={`session-row-${session.game_code}`}
                          >
                            <td className="px-6 py-4 whitespace-nowrap">
                              <span className="font-mono text-sm bg-gray-100 px-2 py-1 rounded">
                                {session.game_code}
                              </span>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <span className={`px-3 py-1 rounded-full text-sm ${getModuleBadgeColor(session.module_id)}`}>
                                {session.module_title || getModuleName(session.module_id)}
                              </span>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-center">
                              <span className={`px-2 py-1 rounded text-xs font-medium ${
                                session.mode === "live" 
                                  ? "bg-green-100 text-green-800" 
                                  : "bg-blue-100 text-blue-800"
                              }`}>
                                {session.mode === "live" ? "🎯 Live" : "🎮 Solo"}
                              </span>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-center">
                              <span className="text-sm font-medium text-gray-900">
                                {session.participant_count}
                              </span>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-center">
                              <span className="text-sm font-bold text-yellow-600">
                                {session.total_points}
                              </span>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-center">
                              <span className="text-sm text-gray-900">
                                {session.avg_score}
                              </span>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-center">
                              <span className="text-sm text-gray-500">
                                {formatDate(session.completed_at)}
                              </span>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            )}
          </>
        )}

        {/* Back Link */}
        <div className="mt-8 text-center">
          <Link 
            to="/dashboard"
            className="text-yellow-600 hover:text-yellow-700 font-medium"
          >
            ← Back to Dashboard
          </Link>
        </div>
      </main>
    </div>
  );
}
